//#region src/pkce.ts
function allocBytes(length) {
	return new Uint8Array(new ArrayBuffer(length));
}
function copyToOwnedBuffer(bytes) {
	const copy = allocBytes(bytes.byteLength);
	copy.set(bytes);
	return copy;
}
const defaultCrypto = {
	random(bytes) {
		const c = globalThis.crypto;
		if (!c?.getRandomValues) throw new Error("globalThis.crypto.getRandomValues is unavailable; supply a custom PkceCryptoAdapter");
		c.getRandomValues(bytes);
		return bytes;
	},
	async sha256(input) {
		const c = globalThis.crypto;
		if (!c?.subtle?.digest) throw new Error("globalThis.crypto.subtle.digest is unavailable; supply a custom PkceCryptoAdapter");
		const buf = await c.subtle.digest("SHA-256", input);
		return new Uint8Array(buf);
	}
};
function base64UrlEncode(bytes) {
	let binary = "";
	for (const byte of bytes) binary += String.fromCharCode(byte);
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function generateVerifier(adapter = defaultCrypto) {
	return base64UrlEncode(adapter.random(allocBytes(32)));
}
async function challengeFromVerifier(verifier, adapter = defaultCrypto) {
	const data = copyToOwnedBuffer(new TextEncoder().encode(verifier));
	return base64UrlEncode(await adapter.sha256(data));
}
async function generatePkcePair(adapter = defaultCrypto) {
	const verifier = generateVerifier(adapter);
	return {
		verifier,
		challenge: await challengeFromVerifier(verifier, adapter)
	};
}
function generateState(adapter = defaultCrypto) {
	return base64UrlEncode(adapter.random(allocBytes(12)));
}
//#endregion
//#region src/cognito.ts
const DEFAULT_SCOPES = [
	"openid",
	"email",
	"profile"
];
function buildAuthorizeUrl(cfg, input) {
	const url = new URL(`${cfg.domain}/oauth2/authorize`);
	url.searchParams.set("response_type", "code");
	url.searchParams.set("client_id", cfg.clientId);
	url.searchParams.set("redirect_uri", input.redirectUri);
	url.searchParams.set("scope", (cfg.scopes ?? DEFAULT_SCOPES).join(" "));
	url.searchParams.set("code_challenge", input.codeChallenge);
	url.searchParams.set("code_challenge_method", "S256");
	url.searchParams.set("state", input.state);
	const identityProvider = input.identityProvider ?? cfg.identityProvider;
	if (identityProvider) url.searchParams.set("identity_provider", identityProvider);
	if (input.forceLogin) url.searchParams.set("prompt", "login");
	return url.toString();
}
var CognitoTokenError = class extends Error {
	code;
	status;
	constructor(message, code, status) {
		super(message);
		this.code = code;
		this.status = status;
		this.name = "CognitoTokenError";
	}
};
async function tokenEndpointError(response, operation) {
	let body = {};
	try {
		body = await response.json();
	} catch {}
	return new CognitoTokenError(operation === "exchange" && body.error === "invalid_grant" ? "That sign-in attempt expired or was already used. Please try again." : operation === "refresh" && body.error === "invalid_grant" ? "Your session expired. Please sign in again." : body.error_description || `Token ${operation} failed. Please try again.`, body.error, response.status);
}
async function exchangeCodeForTokens(cfg, args) {
	const body = new URLSearchParams({
		grant_type: "authorization_code",
		client_id: cfg.clientId,
		code: args.code,
		redirect_uri: args.redirectUri,
		code_verifier: args.verifier
	});
	const response = await fetch(`${cfg.domain}/oauth2/token`, {
		method: "POST",
		headers: { "content-type": "application/x-www-form-urlencoded" },
		body
	});
	if (!response.ok) throw await tokenEndpointError(response, "exchange");
	const json = await response.json();
	return {
		idToken: json.id_token,
		accessToken: json.access_token,
		refreshToken: json.refresh_token,
		expiresAt: Date.now() + json.expires_in * 1e3
	};
}
async function refreshTokens(cfg, refreshToken) {
	const body = new URLSearchParams({
		grant_type: "refresh_token",
		client_id: cfg.clientId,
		refresh_token: refreshToken
	});
	const response = await fetch(`${cfg.domain}/oauth2/token`, {
		method: "POST",
		headers: { "content-type": "application/x-www-form-urlencoded" },
		body
	});
	if (!response.ok) throw await tokenEndpointError(response, "refresh");
	const json = await response.json();
	return {
		idToken: json.id_token,
		accessToken: json.access_token,
		refreshToken: json.refresh_token ?? refreshToken,
		expiresAt: Date.now() + json.expires_in * 1e3
	};
}
function buildLogoutUrl(cfg, args) {
	const url = new URL(`${cfg.domain}/logout`);
	url.searchParams.set("client_id", cfg.clientId);
	url.searchParams.set("logout_uri", args.logoutRedirectUri);
	return url.toString();
}
//#endregion
//#region src/storage.ts
/**
* Conventional storage key for the persisted Cognito token blob, shared by
* the web SPA's localStorage and the Playwright e2e suite that seeds it.
* Hoisted here so the consumers can't drift apart silently — a rename
* surfaces as a TypeScript error in every importer.
*/
const TOKEN_STORAGE_KEY = "tailspend.auth.tokens";
function isCognitoTokens(value) {
	if (typeof value !== "object" || value === null) return false;
	const v = value;
	return typeof v.idToken === "string" && typeof v.accessToken === "string" && typeof v.refreshToken === "string" && typeof v.expiresAt === "number";
}
//#endregion
export { CognitoTokenError, TOKEN_STORAGE_KEY, buildAuthorizeUrl, buildLogoutUrl, challengeFromVerifier, exchangeCodeForTokens, generatePkcePair, generateState, generateVerifier, isCognitoTokens, refreshTokens };

//# sourceMappingURL=index.js.map